import { useEffect, useState } from "react";
import { Dialog, DialogContent, IconButton, Box, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import axiosClient from "../utils/axiosClient";
import { useChatRoom } from "../hooks/useChatRoom";
import ChatWindow from "./ChatWindow";

/**
 * Modal chat window, opened for a given patientId via useChatModal().openChat(patientId).
 * Resolves the patient's chat room id, then reuses the same ChatWindow/useChatRoom
 * used by the full chat page.
 */
const ChatModal = ({ patientId, onClose, showMessage }) => {
  const { t } = useTranslation();
  const [roomId, setRoomId] = useState(null);
  const [isResolvingRoom, setIsResolvingRoom] = useState(false);

  useEffect(() => {
    if (!patientId) {
      setRoomId(null);
      return;
    }
    setIsResolvingRoom(true);
    axiosClient
      .get(`/memberships/${patientId}/chat-room`)
      .then((response) => setRoomId(response?.data?.roomId ?? null))
      .catch((error) => {
        console.error("Error fetching chat room:", error);
        if (showMessage) showMessage(t("something_went_wrong"), () => {}, "error", 4000);
        onClose();
      })
      .finally(() => setIsResolvingRoom(false));
  }, [patientId]);

  const { selectedRoom, messages, isMessagesLoading, chatUserId, sendMessage } = useChatRoom({
    roomId,
    onError: () => {
      if (showMessage) showMessage(t("something_went_wrong"), () => {}, "error", 4000);
    },
  });

  return (
    <Dialog open={!!patientId} onClose={onClose} maxWidth="lg">
      <IconButton
        onClick={onClose}
        aria-label="close"
        sx={{ position: "absolute", right: 8, top: 8, zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 0 }}>
        {isResolvingRoom || !selectedRoom ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", width: "700px", height: "640px" }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <ChatWindow
            selectedRoom={selectedRoom}
            messages={messages}
            isMessagesLoading={isMessagesLoading}
            chatUserId={chatUserId}
            sendMessage={sendMessage}
            showMessage={showMessage}
            sx={{ ml: 0 }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChatModal;
